import type { ReactNode } from 'react';
import { ChevronRight, FileCode2, Folder, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type {
  CheckState,
  ScriptSelectionTreeNode,
} from './scriptSelectionTree';
import {
  fileCheckState,
  folderCheckState,
} from './scriptSelectionTree';

type Props = {
  nodes: ScriptSelectionTreeNode[];
  expandedDirs: Set<string>;
  onToggleDir: (pathKey: string) => void;
  selectedPaths: Set<string>;
  onFolderToggle: (descendantPaths: string[], checked: boolean) => void;
  onFileToggle: (filePath: string, checked: boolean) => void;
  depth?: number;
};

function TreeRow({
  depth,
  expandable,
  expanded,
  isFolder,
  label,
  count,
  checkState,
  onCheckChange,
  onToggleExpand,
}: {
  depth: number;
  expandable: boolean;
  expanded: boolean;
  isFolder: boolean;
  label: string;
  count?: number;
  checkState: CheckState | boolean;
  onCheckChange: (checked: boolean) => void;
  onToggleExpand?: () => void;
}) {
  const checked = checkState === true || checkState === 'checked';
  const indeterminate = checkState === 'indeterminate';

  return (
    <div
      className={cn(
        'group flex h-8 items-center gap-1.5 rounded-md px-2 transition-colors',
        'hover:bg-[var(--inv-accent-soft-2,#edf8ff)]',
        checked && 'bg-[var(--inv-accent-soft)]/40',
      )}
      style={{ paddingLeft: `${0.5 + depth * 0.875}rem` }}
      role="treeitem"
      aria-expanded={expandable ? expanded : undefined}>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border-0 bg-transparent p-0',
          'text-[var(--inv-text-muted)] transition-colors',
          expandable
            ? 'cursor-pointer hover:bg-[var(--inv-surface-muted)] hover:text-[var(--inv-text)]'
            : 'cursor-default opacity-0',
        )}
        onClick={e => {
          e.stopPropagation();
          onToggleExpand?.();
        }}
        disabled={!expandable}
        aria-label={expandable ? (expanded ? 'Collapse' : 'Expand') : undefined}
        tabIndex={expandable ? 0 : -1}>
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-150',
            expanded && 'rotate-90',
          )}
          aria-hidden="true"
        />
      </Button>

      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={onCheckChange}
        className="min-w-0 flex-1 items-center gap-2 py-0 text-[0.8125rem]">
        <span className="inline-flex min-w-0 items-center gap-2">
          {isFolder ? (
            expanded ? (
              <FolderOpen
                className="h-3.5 w-3.5 shrink-0 text-[var(--inv-accent)]"
                aria-hidden="true"
              />
            ) : (
              <Folder
                className="h-3.5 w-3.5 shrink-0 text-[var(--inv-accent)]"
                aria-hidden="true"
              />
            )
          ) : (
            <FileCode2
              className="h-3.5 w-3.5 shrink-0 text-[var(--inv-text-muted)]"
              aria-hidden="true"
            />
          )}
          <span
            className={cn(
              'min-w-0 truncate',
              isFolder
                ? 'font-medium text-[var(--inv-text)]'
                : 'font-normal text-[var(--inv-text-secondary)]',
            )}>
            {label}
          </span>
          {typeof count === 'number' && count > 0 ? (
            <span className="shrink-0 text-[0.6875rem] tabular-nums text-[var(--inv-text-muted)]">
              {count}
            </span>
          ) : null}
        </span>
      </Checkbox>
    </div>
  );
}

function renderNode(
  node: ScriptSelectionTreeNode,
  depth: number,
  expandedDirs: Set<string>,
  onToggleDir: (pathKey: string) => void,
  selectedPaths: Set<string>,
  onFolderToggle: (descendantPaths: string[], checked: boolean) => void,
  onFileToggle: (filePath: string, checked: boolean) => void,
): ReactNode {
  if (node.type === 'file') {
    return (
      <TreeRow
        key={`f:${node.filePath}`}
        depth={depth}
        expandable={false}
        expanded={false}
        isFolder={false}
        label={node.fileName}
        checkState={fileCheckState(node.filePath, selectedPaths)}
        onCheckChange={val => onFileToggle(node.filePath, val)}
      />
    );
  }

  const open = expandedDirs.has(node.pathKey);
  const state = folderCheckState(node.descendantPaths, selectedPaths);

  return (
    <div key={`d:${node.pathKey}`} role="group">
      <TreeRow
        depth={depth}
        expandable={node.children.length > 0}
        expanded={open}
        isFolder
        label={node.name}
        count={node.descendantPaths.length}
        checkState={state}
        onCheckChange={val => {
          const nextChecked = val || state === 'indeterminate';
          onFolderToggle(node.descendantPaths, nextChecked);
        }}
        onToggleExpand={() => onToggleDir(node.pathKey)}
      />
      {node.children.length > 0 && open ? (
        <div className="relative">
          <div
            className="pointer-events-none absolute bottom-1 top-0 w-px bg-[var(--inv-border-soft)]"
            style={{ left: `${0.5 + depth * 0.875 + 0.625}rem` }}
            aria-hidden="true"
          />
          {node.children.map(ch =>
            renderNode(
              ch,
              depth + 1,
              expandedDirs,
              onToggleDir,
              selectedPaths,
              onFolderToggle,
              onFileToggle,
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function ScriptSelectionTreeView({
  nodes,
  expandedDirs,
  onToggleDir,
  selectedPaths,
  onFolderToggle,
  onFileToggle,
  depth = 0,
}: Props) {
  return (
    <div className="flex flex-col gap-px py-1 text-[0.8125rem]" role="tree">
      {nodes.map(n =>
        renderNode(
          n,
          depth,
          expandedDirs,
          onToggleDir,
          selectedPaths,
          onFolderToggle,
          onFileToggle,
        ),
      )}
    </div>
  );
}
